import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import { type DirtyStyle } from '@glitz/react/types/styled/use-glitz';
import * as React from 'react';
import { positiveDark } from 'svenssons/shared/theme';
import { ArrowRight } from 'webapp/components/icons';
import Image from 'webapp/components/image';
import { isSvenssons } from 'webapp/theme';

type Props = {
  productBadges: NordicNest.Api.ProductBadgeApiModel[];
  showProductBadgesFlyout: () => void;
};

type BadgeProps = {
  productBadge: NordicNest.Api.ProductBadgeApiModel;
  additionalBadges?: number;
  onClick?: () => void;
};

export default function ProductPageBadges({ productBadges, showProductBadgesFlyout }: Props) {
  productBadges.sort((a, b) => a.priority - b.priority);
  const firstBadge = productBadges.at(0);
  const nrBadges = productBadges.length;

  return (
    <>
      {firstBadge && (
        <ProductPageBadge productBadge={firstBadge} additionalBadges={nrBadges - 1} onClick={showProductBadgesFlyout} />
      )}
    </>
  );
}

export function ProductPageBadge({ productBadge, additionalBadges = 0, onClick }: BadgeProps) {
  const clickable = productBadge.pdpContent || productBadge.flyoutIntroText || additionalBadges > 0;
  const badgeText = productBadge.badgePublisher
    ? productBadge.text + ' - ' + productBadge.badgePublisher
    : productBadge.text;
  const dirtyCss: DirtyStyle = {
    cursor: clickable ? 'pointer' : undefined,
  };
  if (!productBadge.icon) dirtyCss.gridTemplateColumns = '1fr min-content';

  return (
    <Base onClick={clickable ? onClick : undefined} css={dirtyCss}>
      <Image image={productBadge.icon} preset={'tiny'} />
      <Content>
        <Text>{badgeText}</Text>
        {productBadge.flyoutIntroText && <Intro>{productBadge.flyoutIntroText}</Intro>}
        {additionalBadges > 0 && (
          <More>{translate(t => t.productPage.additionalBadgesText, { nrBadges: additionalBadges })}</More>
        )}
      </Content>
      {clickable && <ArrowRight />}
    </Base>
  );
}

const Base = styled.div({
  display: 'grid',
  minHeight: '80px',
  gridTemplateColumns: '48px 1fr min-content',
  gridGap: t => t.sharedSpacing.s,
  alignItems: 'center',
  padding: { xy: t => t.sharedSpacing.m },
  border: { xy: { style: 'solid', color: t => t.borderStrongColor, width: 1 } },
  ':first-child': {
    alignItems: 'center',
  },
});

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  fontSize: t => t.sharedFontSize.xs,
});

const Text = styled.div({
  fontWeight: isSvenssons ? t => t.fontWeight?.semiBold : t => t.fontWeight?.normal,
});

const Intro = styled.div({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const More = styled.div({
  color: positiveDark,
});
